import { z } from 'zod';

// Documentation-only schemas for the external API responses the contracts still type as z.any().
// They feed the generated Swagger doc (route metadata.responseDoc) and are never used to validate responses.

const BATCH_ID = 'unique identifier of the batch';

const DECLARATION_STATUS =
  'declaration status (ANALYSIS, BEGATE_NOT_RELEASED, BEGATE_RELEASED, BEGATE_SENT, NOT_SENT, SENT, SENDING, ' +
  'ACCEPTED, REJECTED, IN_CONTROL, RELEASED, NOT_RELEASED, INVALIDATED)';

const TaxLineDocSchema = z.object({
  taxType: z.string().describe('A00=duties, B00= VAT, ...').optional(),
  payableTaxAmount: z.number().describe('amount due').optional(),
  paymentMethod: z.string().describe('method Of Payment').optional(),
});

export const ExternalDeclarationDocSchema = z.object({
  _id: z.string().describe('unique identifier for the master declaration on our system (sequence 1)').optional(),
  generalInfo: z
    .object({
      status: z.string().describe(DECLARATION_STATUS).optional(),
      group: z.string().describe('declaration (H1 for High values)').optional(),
    })
    .optional(),
  messageInfo: z
    .object({
      batch: z.string().describe(BATCH_ID).optional(),
      LRN: z.string().describe('LRN number of the declaration').optional(),
      declarationType: z.string().describe('Type (A, Y, ...)').optional(),
      declarationMainType: z.string().describe('Main Type (IM, ...)').optional(),
    })
    .optional(),
  stakeholders: z
    .object({
      exporter: z
        .object({
          name: z.string().describe('exporter name').optional(),
        })
        .optional(),
      importer: z
        .object({
          name: z.string().describe('importer name').optional(),
        })
        .optional(),
    })
    .optional(),
  consignment: z
    .object({
      referenceNumberUCR: z.string().describe('Commercial reference number of the declaration').optional(),
    })
    .optional(),
  declarationGlobal: z
    .object({
      batchName: z.string().describe('batch name').optional(),
      customerName: z.string().describe('customer name').optional(),
      totalPackages: z.number().int().describe('total number of packages in this declaration').optional(),
      totalQuantity: z.number().int().describe('total quantity in this declaration').optional(),
      grossMass: z.number().describe('gross mass in this declaration').optional(),
      netMass: z.number().describe('net mass in this declaration').optional(),
      numberOfArticles: z.number().int().describe('number of declaration lines in this declaration').optional(),
    })
    .optional(),
  customsState: z
    .object({
      movementReference: z.string().describe('movement reference number (returned by customs)').optional(),
      releaseForImport: z
        .object({
          dateOfRelease: z.string().describe('date of release YYYY-MM-DD').optional(),
        })
        .optional(),
      calculationResults: z
        .object({
          total: z.array(TaxLineDocSchema).optional(),
        })
        .optional(),
      inControl: z
        .object({
          notificationDate: z.string().describe('notification date YYYY-MM-DD').optional(),
          controlType: z.string().describe('control type').optional(),
          customsRegistrationNumber: z.string().describe('customs registration number').optional(),
          notificationType: z.string().describe('notification type').optional(),
          anticipatedControlDate: z.string().describe('anticipated control date').optional(),
          text: z.string().describe('text').optional(),
          typeOfControls: z
            .array(
              z.object({
                type: z.string().describe('type of control').optional(),
                remarks: z.string().describe('control remarks').optional(),
              }),
            )
            .describe('type of controls and remarks')
            .optional(),
          requestedDocuments: z
            .array(
              z.object({
                type: z.string().describe('type of document').optional(),
                ccQualifier: z.string().describe('cc qualifier').optional(),
                referenceNumber: z.string().describe('reference number').optional(),
                description: z.string().describe('description').optional(),
              }),
            )
            .optional(),
        })
        .optional(),
      controlResult: z
        .array(
          z.object({
            code: z.string().describe('control result code').optional(),
            date: z.string().describe('control result date').optional(),
            remarks: z.string().describe('remarks').optional(),
            resultsOfControl: z
              .array(
                z.object({
                  riskAreaCode: z.string().describe('risk area code').optional(),
                  controlType: z.string().describe('control type').optional(),
                  controlDate: z.string().describe('control date').optional(),
                  remarks: z.string().describe('remarks').optional(),
                  controlDetails: z
                    .array(
                      z.object({
                        typeOfDiscrepancies: z.string().describe('type of discrepancies').optional(),
                        attributePointer: z.string().describe('attribute pointer').optional(),
                        correctedValue: z.string().describe('corrected value').optional(),
                        remarks: z.string().describe('remarks').optional(),
                      }),
                    )
                    .optional(),
                }),
              )
              .optional(),
          }),
        )
        .optional(),
      prelodge: z
        .object({
          correlationId: z.string().describe('correlation id').optional(),
          acceptanceDate: z.string().describe('acceptance date').optional(),
          presentationNotificationDueDate: z.string().describe('presentation notification due date').optional(),
          mrn: z.string().describe('movement reference number').optional(),
          status: z.string().describe('status').optional(),
        })
        .optional(),
      sentTo: z.string().describe('the system the declaration was sent to').optional(),
      flow: z.string().describe('flow').optional(),
      functionalError: z
        .array(
          z.object({
            sequenceNumber: z.number().describe('sequence number of the declaration line').optional(),
            errorPointer: z.string().describe('error pointer').optional(),
            errorCode: z.string().describe('error code').optional(),
            errorReason: z.string().describe('error reason').optional(),
            remarks: z.string().describe('optional').optional(),
            type: z.string().describe('optional').optional(),
          }),
        )
        .optional(),
    })
    .optional(),
});

export const ExternalDeclarationLineDocSchema = z.object({
  _id: z.string().describe('unique identifier for the declaration line on our system. Declaration lines are grouped by HS code, description, ...').optional(),
  messageInfo: z
    .object({
      batch: z.string().describe(BATCH_ID).optional(),
      LRN: z.string().describe('LRN number of the declaration').optional(),
    })
    .optional(),
  goodsShipmentItems: z
    .array(
      z.object({
        invoiceLines: z
          .array(
            z.object({
              goodsItemLineNumber: z.number().int().describe('invoiced line number').optional(),
              totalAmountInvoiced: z.number().describe('total amount invoiced line').optional(),
              quantity: z.number().int().describe('quantity invoiced line').optional(),
              numberOfPackages: z.number().int().describe('number of packages invoiced line').optional(),
              netMass: z.number().describe('net mass invoiced line').optional(),
              grossMass: z.number().describe('gross mass invoiced line').optional(),
              shippingMarks: z.string().describe('shipping mark invoiced line').optional(),
              trackingId: z.string().describe('tracking id invoiced line').optional(),
              invoicedAmountPerKg: z.number().describe('amount per kg invoiced line').optional(),
            }),
          )
          .optional(),
        packaging: z
          .object({
            numberOfPackages: z.number().int().describe('Total number of Packages of the distinct goods item').optional(),
            typeOfPackage: z.number().int().describe('package type (PK, PC, CT, ...)').optional(),
            shippingMarks: z.string().describe('shipping mark of the distinct goods item').optional(),
          })
          .optional(),
        evalMethodDescription: z
          .object({
            suggestedDescriptionOfGoods: z.string().describe('operator suggested description of goods for the distinct goods item').optional(),
          })
          .optional(),
        evalMethodAiHsCode: z
          .object({
            suggestedHsCode: z.string().describe('operator suggested HS code for the distinct goods item').optional(),
          })
          .optional(),
        suggestedValues: z
          .object({
            referenceValue: z.string().describe('reference value can be from INVOICE or WEB or IMADB').optional(),
            dutyRate: z.number().describe('estimated duty rate based on the HS code (suggested from operator, and if not, based on the invoice)').optional(),
            unitValue: z.number().describe('unit value based on reference').optional(),
            totalValue: z.number().describe('total value based on reference').optional(),
            payableDuty: z.number().describe('estimated duties based on reference').optional(),
            payableVat: z.number().describe('estimated VAT based on reference').optional(),
            totalDutiesAndTaxesAmount: z.number().describe('cumulated amount (duty and VAT) based on reference').optional(),
            adjustedDuty: z.number().describe('estimated duties based on the new duty rate (if a new HS code is suggested)').optional(),
            adjustedVat: z.number().describe('estimated VAT based on the new duty rate (if a new HS code is suggested)').optional(),
            adjustedTotalDutiesAndTaxesAmount: z
              .number()
              .describe('cumulated amount (duty and VAT) based on the new duty rate (if a new HS code is suggested)')
              .optional(),
            additionalDuties: z.number().describe('estimated delta duties with suggested HS code').optional(),
            additionalVat: z.number().describe('estimated delta VAT with suggested HS code').optional(),
            additionalDutiesAndTaxesAmount: z.number().describe('cumulated amount (duty and VAT) with suggested HS code').optional(),
            vatRate: z.number().describe('VAT rate (fixed value 21% for Belgium)').optional(),
          })
          .describe('suggested values for this distinct goods item')
          .optional(),
        invoiceCurrency: z.string().describe('reference currency unit of the invoice (eg. USD, EUR)').optional(),
        exchangeRate: z.number().describe('reference rate of the currency exchange with Euro').optional(),
      }),
    )
    .describe('goods items are grouped by distinct criteria (amountperKg, url, ...)')
    .optional(),
  goodShipmentGlobal: z
    .object({
      totalQuantity: z.number().describe('sum of quantities for all goods items').optional(),
      totalAmountInvoiced: z.number().describe('sum of total amount for all goods items').optional(),
      totalGrossMass: z.number().describe('sum of gross mass for all goods items').optional(),
      totalNetMass: z.number().describe('sum of net mass for all goods items').optional(),
      totalNumberOfPackages: z.number().describe('sum of number of packages for all goods items').optional(),
      invoiceCurrency: z.string().describe('reference currency unit of the invoice (eg. USD, EUR)').optional(),
      firstItemData: z
        .object({
          hsCode: z.string().describe('HS code of the first goods item').optional(),
          invoiceDescription: z.string().describe('invoice description').optional(),
        })
        .optional(),
      sequence: z.number().int().describe('position of declaration line in a declaration used for customs').optional(),
    })
    .describe('consolidation of all goods items in the declaration line')
    .optional(),
  consignment: z
    .object({
      referenceNumberUCR: z.string().describe('Commercial reference number of the declaration').optional(),
    })
    .optional(),
  customsState: z
    .object({
      movementReference: z.string().describe('movement reference number (returned by customs)').optional(),
      releaseForImport: z
        .object({
          dateOfRelease: z.string().describe('date of release YYYY-MM-DD').optional(),
        })
        .optional(),
      calculationResults: z
        .object({
          items: z
            .array(
              TaxLineDocSchema.extend({
                taxBase: z
                  .object({
                    taxRate: z.number().describe('tax rate').optional(),
                    amount: z.number().describe('amount of the customs value').optional(),
                    taxAmount: z.number().describe('tax amount calculated by customs').optional(),
                  })
                  .optional(),
              }),
            )
            .describe('total duties and VAT calculated by customs for this declaration line')
            .optional(),
          total: z.array(TaxLineDocSchema).describe('total duties and VAT to be paid for the declaration').optional(),
        })
        .optional(),
    })
    .optional(),
});
